import { COSMETICS, type CosmeticType } from '@/models/cosmetics';
import type { CosmeticsManager } from '@/systems/CosmeticsManager';

type Tab = 'frame' | 'badge' | 'name_color';

const TAB_LABELS: Record<Tab, string> = {
  frame: 'Frames',
  badge: 'Badges',
  name_color: 'Colors',
};

const TABS: Tab[] = ['frame', 'badge', 'name_color'];

export class CosmeticsPanel {
  private container: HTMLElement;
  private listEl: HTMLElement;
  private activeTab: Tab = 'frame';
  private _manager: CosmeticsManager | null = null;
  private _onEquip: (id: string) => void;

  constructor(parent: HTMLElement, cosmeticsManager: CosmeticsManager, onEquip: (id: string) => void) {
    this._manager = cosmeticsManager;
    this._onEquip = onEquip;

    this.container = document.createElement('div');
    this.container.id = 'cosmetics-panel';
    this.container.className = 'game-panel';

    // Header
    const header = document.createElement('div');
    header.className = 'game-panel-header';
    header.innerHTML = `
      <div class="game-panel-title">COSMETICS</div>
      <button class="game-panel-close">&times;</button>
    `;
    header.querySelector('.game-panel-close')!.addEventListener('click', () => this.hide());
    this.container.appendChild(header);

    // Tabs
    const tabs = document.createElement('div');
    tabs.className = 'achievement-tabs';
    for (const tab of TABS) {
      const btn = document.createElement('button');
      btn.className = 'achievement-tab' + (tab === this.activeTab ? ' active' : '');
      btn.dataset['tab'] = tab;
      btn.textContent = TAB_LABELS[tab];
      btn.addEventListener('click', () => {
        this.activeTab = tab;
        tabs.querySelectorAll('.achievement-tab').forEach(t => t.classList.remove('active'));
        btn.classList.add('active');
        this.renderList();
      });
      tabs.appendChild(btn);
    }
    this.container.appendChild(tabs);

    // Item list
    this.listEl = document.createElement('div');
    this.listEl.className = 'achievement-list';
    this.container.appendChild(this.listEl);

    parent.appendChild(this.container);
  }

  update(manager: CosmeticsManager): void {
    this._manager = manager;
    this.renderList();
  }

  private renderList(): void {
    if (!this._manager) return;
    const manager = this._manager;

    this.listEl.innerHTML = '';
    const items = COSMETICS.filter(c => c.type === this.activeTab);
    const equipped = manager.getEquipped();

    for (const item of items) {
      const isOwned = manager.owns(item.id);
      const isEquipped = equipped[item.type as CosmeticType] === item.id;

      const row = document.createElement('div');
      row.className = 'cosmetic-item' + (isOwned ? '' : ' locked');

      // Preview
      const preview = document.createElement('div');
      preview.className = 'cosmetic-preview';
      if (item.type === 'badge') {
        preview.textContent = item.value || '—';
      } else if (item.type === 'name_color') {
        preview.textContent = 'A';
        preview.style.color = item.value === 'rainbow' ? 'transparent' : item.value;
        if (item.value === 'rainbow') {
          preview.style.backgroundImage = 'linear-gradient(90deg, #e74c3c, #e8d44d, #4a9c5e, #5b9bd5, #a855f7)';
          preview.style.webkitBackgroundClip = 'text';
          preview.style.backgroundClip = 'text';
        }
      } else {
        // frame: show border style hint
        preview.textContent = '⬜';
      }

      // Info
      const info = document.createElement('div');
      info.style.flex = '1';
      info.style.minWidth = '0';

      const name = document.createElement('div');
      name.className = `cosmetic-name cosmetic-rarity-${item.rarity}`;
      name.textContent = item.name;

      const source = document.createElement('div');
      source.style.fontSize = '10px';
      source.style.color = 'var(--text-dim)';
      source.textContent = isOwned ? item.rarity : `🔒 ${item.source}`;

      info.appendChild(name);
      info.appendChild(source);

      // Equip button
      const btn = document.createElement('button');
      btn.className = 'btn btn-upgrade';
      if (!isOwned) {
        btn.textContent = 'Locked';
        btn.disabled = true;
      } else if (isEquipped) {
        btn.textContent = 'Equipped';
        btn.disabled = true;
      } else {
        btn.textContent = 'Equip';
        btn.addEventListener('click', () => {
          this._onEquip(item.id);
          if (this._manager) this.update(this._manager);
        });
      }

      row.appendChild(preview);
      row.appendChild(info);
      row.appendChild(btn);
      this.listEl.appendChild(row);
    }
  }

  show(): void {
    this.container.style.display = 'block';
    this.renderList();
  }

  hide(): void {
    this.container.style.display = 'none';
  }

  toggle(): void {
    if (this.container.style.display === 'none') {
      this.show();
    } else {
      this.hide();
    }
  }

  destroy(): void {
    this.container.remove();
  }
}
